import React, {Fragment, useState} from 'react';
import styled from 'styled-components';
import Icon from '@mdi/react';
import SGSubtitle from 'components/Typography/SGSubtitle';
import RegisterSummary from 'features/POSRegisters/POSTable/Registers/components/Summary/RegisterSummary';
import Alert from 'features/POSRegisters/components/Alert';
import POSState from 'features/POSRegisters/interfaces/POSState';
import Divider from '@material-ui/core/Divider';
import {mdiPuzzle} from '@mdi/js';
import {DeviceTypeString} from 'features/POSRegisters/interfaces/DeviceType';
import {getDeviceList} from 'features/POSRegisters/functions/getDeviceList';
import {
  hasAssociation,
  checkIsScanFailureAlert,
  checkIsOrphanDeviceAlert,
  checkIsConfigurationDeviceAlert,
} from 'features/POSRegisters/functions/alertTypes';
import ModalButton from 'features/POSRegisters/components/ModalButton';
import Device from 'features/POSRegisters/interfaces/Device';
import DeviceType from 'features/POSRegisters/interfaces/DeviceType';
import TypedDevice from 'features/POSRegisters/interfaces/TypedDevice';
import ScanDetails, {Scan} from 'features/POSRegisters/interfaces/ScanDetails';
import AlertType from 'features/POSRegisters/interfaces/AlertInfo';
import DeviceModal from 'features/POSRegisters/components/DeviceModal';

//#region Styled Components

const StyledAnomaliesDivider = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto 1fr;
  align-items: center;
  gap: 6px;
  margin: 24px 0;

  > hr {
    background-color: ${props => props.theme.disabled};
  }

  > svg {
    color: ${props => props.theme.grey};
    margin-left: 16px;
  }

  > p {
    font-family: 'Trade Gothic Bold Condensed';
    text-transform: uppercase;
    font-weight: bold;
    margin-right: 16px;
  }
`;

const StyledAnomalyAlerts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
//#endregion Styled Components

export default function Summary(state: POSState) {
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectedDevice, setSelectedDevice] = useState<DeviceType | null>(null);
  const [selectedTypedDevice, setSelectedTypedDevice] = useState<TypedDevice<ScanDetails>>();

  const devices = [
    ...state.ipads?.map(device => ({...device, type: 'ipad' as DeviceType})),
    ...state.ipadsMini?.map(device => ({...device, type: 'ipadMini' as DeviceType})),
    ...state.receiptPrinters?.map(device => ({...device, type: 'receiptPrinter' as DeviceType})),
    ...state.cashDrawers?.map(device => ({...device, type: 'cashDrawer' as DeviceType})),
    ...state.barcodeScanners?.map(device => ({...device, type: 'barcodeScanner' as DeviceType})),
    ...state.paymentTerminals?.map(device => ({...device, type: 'paymentTerminal' as DeviceType})),
  ].filter(device => device?.alerts?.length);

  const getAnomalies = (device: Device<ScanDetails>, checkFn: (alert: AlertType) => boolean) =>
    device?.alerts?.filter(alert => checkFn(alert) && !hasAssociation(device?.scanResults));

  const hasAnomalies = devices.some(device => {
    return device.alerts.some((alert: AlertType) => {
      return (
        !hasAssociation(device?.scanResults) &&
        (checkIsScanFailureAlert(alert) ||
          checkIsOrphanDeviceAlert(alert) ||
          checkIsConfigurationDeviceAlert(alert))
      );
    });
  });

  const handleOpen = (deviceType: DeviceType, selectedTypedDevice: TypedDevice<ScanDetails>) => {
    setOpenModal(true);
    setSelectedDevice(deviceType);
    setSelectedTypedDevice(selectedTypedDevice);
  };

  const handleClose = () => {
    setOpenModal(false);
  };

  return (
    <>
      {state.registers
        .filter(register => {
          const devices = getDeviceList(register);
          return devices.some(device => hasAssociation(device?.scanResults));
        })
        .map(register => (
          <RegisterSummary key={register.id} {...register} />
        ))}

      {hasAnomalies && (
        <StyledAnomaliesDivider>
          <Divider />

          <Icon path={mdiPuzzle} size={0.625} />
          <SGSubtitle variant="s2">Anomalies</SGSubtitle>

          <Divider />
        </StyledAnomaliesDivider>
      )}

      <StyledAnomalyAlerts>
        {devices.map((device, deviceIdx) => (
          <Fragment
            key={`scan-failure-${device.type}-${device.scanResults?.deviceName}-${deviceIdx}`}
          >
            {getAnomalies(device, checkIsScanFailureAlert).map((alert, alertIdx) => (
              <Alert
                key={`scan-failure-alerts-${deviceIdx}-${device.scanResults?.deviceName}-${alertIdx}`}
                device={device}
                alert={alert}
                rows={[
                  {
                    label: 'Device Type',
                    value: DeviceTypeString[device.type],
                  },
                  {
                    label: 'Device Name',
                    value: device.scanResults.deviceName,
                  },
                  {label: 'Model', value: device.scanResults.model},
                  {label: 'Serial Number', value: device.scanResults.serialNumber},
                  {label: 'Issue', value: alert.description, fullWidth: true},
                ]}
              />
            ))}
          </Fragment>
        ))}

        {devices.map((device, deviceIdx) => (
          <Fragment
            key={`orphan-devices-${device.type}-${device.scanResults?.deviceName}-${deviceIdx}`}
          >
            {getAnomalies(device, checkIsOrphanDeviceAlert).map((alert, alertIdx) => (
              <Alert
                key={`orphan-devices-alerts-${device.type}-${device.scanResults?.deviceName}-${alertIdx}`}
                device={device}
                alert={alert}
                rows={[
                  {
                    value: (
                      <ModalButton onClick={() => handleOpen(device.type, device)}>
                        VIEW DEVICE INFORMATION
                      </ModalButton>
                    ),
                    fullWidth: true,
                  },
                  {
                    label: 'Device Type',
                    value: DeviceTypeString[device.type],
                  },
                  {
                    label: 'Device Name',
                    value: device.scanResults.deviceName,
                  },
                  {label: 'Model', value: device.scanResults.model},
                  {label: 'Serial Number', value: device.scanResults.serialNumber},
                  {label: 'Issue', value: alert.description, fullWidth: true},
                  {label: 'Effect', value: alert.effect, fullWidth: true},
                  {label: 'Solution', value: `${alert.solution} ${alert.kb}`, fullWidth: true},
                ]}
              />
            ))}
          </Fragment>
        ))}

        {devices.map((device, deviceIdx) => (
          <Fragment
            key={`configuration-${device.type}-${device.scanResults?.deviceName}-${deviceIdx}`}
          >
            {getAnomalies(device, checkIsConfigurationDeviceAlert).map((alert, alertIdx) => (
              <Alert
                key={`configuration-alerts-${deviceIdx}-${device.scanResults?.deviceName}-${alertIdx}`}
                device={device}
                alert={alert}
                rows={[
                  {
                    label: 'Device Type',
                    value: DeviceTypeString[device.type],
                  },
                  {
                    label: 'Device Name',
                    value: device.scanResults.deviceName,
                  },
                  {label: 'Model', value: device.scanResults.model},
                  {label: 'Serial Number', value: device.scanResults.serialNumber},
                  {label: 'Issue', value: alert.description, fullWidth: true},
                  {label: 'Effect', value: alert.effect, fullWidth: true},
                  {label: 'Solution', value: `${alert.solution} ${alert.kb}`, fullWidth: true},
                ]}
              />
            ))}
          </Fragment>
        ))}
      </StyledAnomalyAlerts>
      <DeviceModal
        selectedDevice={selectedDevice}
        modalState={openModal}
        handleClose={handleClose}
        typedDevice={selectedTypedDevice}
      />
    </>
  );
}
